// @flow
import { range } from 'lodash';
import { useCallback, useMemo, useState } from 'react';

export type OnGoToPageFunction = (page: number) => void;
export type SetRowsPerPageFunction = (rows: number) => void;

export type PaginationType = {
    currentPage: number,
    pages: number[],
    totalPages: number,
    pageSize: number,
    goToNextPage: () => void,
    goToPreviousPage: () => void,
    goToPage: OnGoToPageFunction,
    setRowsPerPage: SetRowsPerPageFunction,
    onChangePage: (_: unknown, page: number) => void,
};

const usePagination = (total: number, initialPageSize: number = 15, firstPageIndex = 1): PaginationType => {
    const [currentPage, setCurrentPage] = useState(firstPageIndex);
    const [pageSize, setRowsPerPage] = useState(initialPageSize);

    const totalPages = useMemo(() => Math.ceil(total / pageSize), [pageSize, total]);
    const pages = useMemo(() => range(1, totalPages + 1), [totalPages]);

    const goToNextPage = useCallback(() => setCurrentPage((page) => page + 1), []);
    const goToPreviousPage = useCallback(() => setCurrentPage((page) => page - 1), []);
    const goToPage = useCallback((page: number) => setCurrentPage(Math.max(page, firstPageIndex)), [firstPageIndex]);

    const onChangePage = useCallback((_, page: number) => goToPage(page), [goToPage]);

    return {
        currentPage,
        pages,
        totalPages,
        pageSize,
        goToNextPage,
        goToPreviousPage,
        goToPage,
        setRowsPerPage,
        onChangePage,
    };
};

export default usePagination;
